import type { CSSProperties, ReactNode } from 'react';
import { Suspense } from 'react';
import { cn } from 'app/lib/utils';

// antd's own control heights. The placeholder reserves the same box so a picker does not
// shift the layout around it while its chunk is in flight.
const heightForSize = {
  small: 'h-6',
  middle: 'h-8',
  large: 'h-10',
} as const;

export interface PickerSuspenseProps {
  size?: keyof typeof heightForSize;
  // The picker's own style; its width is mirrored so the box does not resize on load.
  style?: CSSProperties;
  children: ReactNode;
}

// Shared by the picker façades so the lazy boundary — and the box it holds open — stays
// identical across them. Not exported from the barrel: it is an implementation detail.
//
// The picker's className is deliberately NOT forwarded here. Callers pass width classes to
// tell antd how to size its input, and `w-auto` on an empty div collapses it to its borders
// — a 2px sliver where the field should be.
export const PickerSuspense = ({
  size = 'middle',
  style,
  children,
}: PickerSuspenseProps) => (
  <Suspense
    fallback={
      <div
        role="status"
        style={style?.width == null ? undefined : { width: style.width }}
        className={cn(
          'w-full animate-pulse rounded-md border border-solid border-gray-200 bg-gray-50',
          heightForSize[size]
        )}
      >
        <span className="sr-only">Loading date field</span>
      </div>
    }
  >
    {children}
  </Suspense>
);
